import constant from "@/utils/constant";

export interface Message {
    code: string,
    msg?: string,
    data?: object,
}

export interface MessageHandle {
    name: string,
    group: string, // 分组是为了区分不同的功能，区别于 default，每组对应一个 msgCode
    msgCode?: string[], // 非默认组的消息，需要指定消息码
    enableDefault: boolean, // 当前执行器执行之后，是否允许执行默认执行器
    handles: Array<(msg: Message) => void>
}

export interface WebsocketConfig {
    wsConn?: string,
    reConn?: boolean,
    maxConnTime?: number,
    reConnTimeout?: number,
    heartCheck?: boolean,
    heartCheckTimeout?: number,
}

interface WebsocketTool {
    ws: WebSocket,
    wsConfig: WebsocketConfig,
    handles: Map<string, MessageHandle[]>,
    handleCodeMap: Map<string, Set<string>>, // code => groupName[]
    init: (userId: string, token: string, defaultCallback: (msg: Message) => void, config?: WebsocketConfig) => void,
    conn: () => void,
    open: () => void,
    close: () => void,
    reConnect: () => void,
    message: () => void,
    send: (msg: Message) => void,
    setHandles: (groupName: string, handle: MessageHandle[]) => void,
    addHandle: (handleName: string, groupName: string, callback: (msg: Message) => void, msgCode?: string[], enableDefault?: boolean) => void,
    delHandle: (handleName: string, groupName: string) => void,
    buildHandleMap: () => void,
}

export interface CodeInfoMap {
    title: string,
    pagePath?: string
}

export const codeMap: Map<string, CodeInfoMap> = new Map<string, CodeInfoMap>([
    ["device_message", {title: "设备消息", pagePath: "ServerDeviceDeviceIndex"}],
    ["user_message", {title: "用户消息", pagePath: "SysUserIndex"}],

    ["login_message", {title: "登陆消息", pagePath: "MonitorServiceIndex"}],
    ["login_out_message", {title: "登出消息", pagePath: "MonitorServiceIndex"}],
    ["single_login_message", {title: "登陆消息", pagePath: "MonitorServiceIndex"}],
    ["connect_message", {title: "连接消息", pagePath: "MonitorServiceIndex"}],
    ["disconnect_message", {title: "断连消息", pagePath: "MonitorServiceIndex"}],

    ["scene_message", {title: "场景消息", pagePath: "ServerSceneInfoIndex"}],
    ["quartz_message", {title: "定时器消息", pagePath: "QuartzScheduleIndex"}],
    ["delay_message", {title: "倒计时器消息", pagePath: "ServerSceneInfoIndex"}],

    ["im_message", {title: "即时消息", pagePath: "AppIMInfoIndex"}]
])

let timerInterval: number = 1
let reConnTimeOut: number = 2
let currReConnTime: number = 0
export const websocketTool: WebsocketTool = {
    // @ts-ignore
    ws: WebSocket,
    wsConfig: {
        wsConn: "",
        reConn: true,
        maxConnTime: 20,
        reConnTimeout: 5000,
        heartCheck: true,
        heartCheckTimeout: 5000,
    },
    handles: new Map<string, MessageHandle[]>(),
    handleCodeMap: new Map<string, Set<string>>(), // 存储 msgCode 对应的handleGroup
    init(userId: string, token: string, defaultCallback: (msg: Message) => void, config?: WebsocketConfig): void {
        if (!userId) {
            return;
        }
        if (!!defaultCallback) {
            // 这里避免外部已经初始化过了 default
            this.addHandle('default', "default", defaultCallback)
        }
        this.wsConfig = {
            ...this.wsConfig, ...config,
            wsConn: `${constant.wsUrl}/${userId}/${token}/${Math.floor(Math.random() * 10000)}`
        }
        this.conn()
    },
    conn() {
        if (this.wsConfig.wsConn) {
            this.ws = new WebSocket(this.wsConfig.wsConn);
            this.message()
            this.open()
            this.close()
        }
    },
    open() {
        this.ws.onopen = (evt: Event) => {
            console.debug("Connection open ...");
            clearTimeout(reConnTimeOut)
            currReConnTime = 0
            if (this.wsConfig.heartCheck) {
                // 创建循环定时器 心跳
                timerInterval = setInterval(
                    () => {
                        this.send({code: "heart_check"})
                    }, this.wsConfig.heartCheckTimeout)
            }
        };
    },
    close() {
        this.ws.onclose = (evt: Event) => {
            console.debug("Connection closed.");
            clearInterval(timerInterval)
            this.reConnect()
        };
    },
    reConnect() {
        if (this.wsConfig.reConn) {
            // 断连重连
            currReConnTime++
            // @ts-ignore
            if (currReConnTime <= this?.wsConfig?.maxConnTime) {
                console.debug(`第${currReConnTime}次重连中...`)
                reConnTimeOut = setTimeout(
                    () => {
                        this.conn()
                    }, this.wsConfig.reConnTimeout)
            } else {
                throw new Error("重连次数过多")
            }
        }
    },
    message() {
        const that = this
        this.ws.onmessage = function (evt: Event) {
            console.debug("Received Message: " + evt);
            // handle 处理，解析code或者，约定消息格式
            const {data} = (evt as any)
            try {
                // TODO 这里需要更多处理逻辑
                const msg: Message = JSON.parse(data)
                const handles = new Array<MessageHandle>()
                // 获取所有匹配的handle
                that.handleCodeMap.get(msg.code)?.forEach(group => {
                    handles.push(...that.handles.get(group) || [])
                })

                // 执行默认
                const execDefault = () => {
                    that.handles.get("default")?.forEach(handle => {
                        console.debug(`Execute default Handle[group:${handle.group};name:${handle.name}] for Message[${JSON.stringify(msg)}]`)
                        handle.handles.forEach(i => i(msg))
                    })
                }

                if (handles && handles.length > 0) {
                    handles.forEach(handle => {
                        if (handle.enableDefault) {
                            execDefault()
                        }
                        // 执行当前处理器
                        console.debug(`Execute Handle[group:${handle.group};name:${handle.name}] for Message[${JSON.stringify(msg)}]`)
                        handle.handles.forEach(i => i(msg))
                    })
                } else {
                    // 如果没有匹配到执行器，就执行默认组里面的所有方法
                    execDefault()
                }
            } catch (e) {
                console.error(`parse websocket message to json fail: ${e}`)
            }
        };
    },
    send(msg: Message) {
        this.ws.send(JSON.stringify(msg));
    },
    /**
     * 设置整个组的处理器
     * @param groupName
     * @param handles
     */
    setHandles(groupName: string, handles: MessageHandle[]) {
        if (!groupName || !handles) {
            return
        }
        this.handles.set(groupName, handles)
    },
    /**
     * 往某个组里添加一个处理器
     * @param handleName
     * @param groupName
     * @param callback
     * @param msgCode 这个组对应的 msgCode，如果为空，则可能处理不了，为空只限于已经初始化过的情况和default
     * @param enableDefault
     */
    addHandle(handleName: string, groupName: string, callback: (msg: Message) => void, msgCode?: string[], enableDefault: boolean = true) {
        if (!handleName || !groupName || !callback) {
            return
        }
        if (this.handles.get(groupName)) {
            const messageHandles = this.handles.get(groupName);
            // 判断是否已存在这个handle
            let messageHandle = messageHandles?.find(i => i.name === handleName);
            if (!messageHandle) {
                // @ts-ignore
                messageHandles.push({
                    name: handleName,
                    group: groupName,
                    msgCode: msgCode,
                    enableDefault: enableDefault,
                    handles: new Array(callback)
                })
            }
            // @ts-ignore
            this.setHandles(groupName, messageHandles)
        } else {
            this.handles.set(groupName, [{
                name: handleName,
                group: groupName,
                msgCode: msgCode,
                enableDefault: enableDefault,
                handles: new Array(callback)
            }])
        }
        console.debug("Current Message Handles", this.handles)
        this.buildHandleMap()
    },
    /**
     * 删除某个组的某个处理器
     * @param handleName
     * @param groupName
     */
    delHandle(handleName: string, groupName: string = "default") {
        if (!handleName) {
            return
        }
        if (this.handles.get(groupName)) {
            let messageHandles = this.handles.get(groupName);
            // @ts-ignore
            messageHandles = messageHandles.filter(i => i.name != handleName)
            // @ts-ignore
            this.handles.set(groupName, messageHandles)
        }
        console.debug("Current Message Handles", this.handles)
        this.buildHandleMap()
    },
    buildHandleMap() {
        // 构架一个msgCode与处理器的对应关系,default 除去
        this.handleCodeMap = new Map<string, Set<string>>()
        this.handles.forEach((handles, groupName) => {
            if (groupName != "default") {
                const {handleCodeMap} = this
                handles.forEach(handle => {
                    handle.msgCode?.forEach(code => {
                        handleCodeMap.set(code, new Set<string>([...handleCodeMap.get(code) || [], groupName]))
                    })
                })
                console.debug("curr handle CodeMap", handleCodeMap)
            }
        })
    }
}
